import { useTranslation } from 'react-i18next';
import { ErrorDeApi } from '../../api/cliente';
import { Boton } from './basicos';

/**
 * Los tres estados de una consulta, diseñados y no improvisados (D9).
 *
 * El de error es el que más importa: muestra el texto que corresponde al **código** que
 * devolvió el servidor —nunca lo que venga en el cuerpo— y ofrece reintentar. Y deja el
 * identificador de correlación a la vista, que es lo que convierte un "no anda" en algo
 * que se puede buscar en el registro del servidor.
 */
export function Cargando({ texto }: { readonly texto?: string }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <p role="status" className="py-6 text-center text-sm text-texto-tenue">
      {texto ?? t('estados.cargando')}
    </p>
  );
}

export function Vacio({
  titulo,
  descripcion,
  accion,
}: {
  readonly titulo?: string | undefined;
  readonly descripcion?: string | undefined;
  readonly accion?: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center">
      <p className="text-sm font-medium">{titulo ?? t('estados.vacio')}</p>
      {descripcion === undefined ? null : (
        <p className="max-w-sm text-xs text-texto-tenue">{descripcion}</p>
      )}
      {accion}
    </div>
  );
}

export function Problema({
  error,
  reintentar,
}: {
  readonly error: unknown;
  readonly reintentar?: (() => void) | undefined;
}): React.ReactElement {
  const { t } = useTranslation();
  const codigo = error instanceof ErrorDeApi ? error.codigo : 'ERR_INTERNO';
  const correlacion = error instanceof ErrorDeApi ? error.correlacion : null;

  return (
    <div role="alert" className="flex flex-col items-start gap-2 py-6">
      <p className="text-sm text-mal">{t(`errores:${codigo}`)}</p>
      {correlacion === null ? null : (
        <p className="numero text-xs text-texto-tenue">{correlacion}</p>
      )}
      {reintentar === undefined ? null : (
        <Boton onClick={reintentar}>{t('acciones.reintentar')}</Boton>
      )}
    </div>
  );
}

/**
 * Traduce un código de error para mostrarlo en línea, junto a un campo o un botón.
 *
 * **Los `detalles` que manda el servidor entran como valores de interpolación.** Sin eso, un
 * mensaje como "Debe {{saldo}}, el límite es {{limite}}" llega al mostrador con las llaves a la
 * vista: i18next deja el marcador crudo cuando falta el valor, no lo borra. Nueve de los
 * mensajes de error del catálogo tienen marcadores y todos se veían así.
 */
export function useTextoDeError(): (error: unknown) => string {
  const { t } = useTranslation();
  return (error: unknown) =>
    error instanceof ErrorDeApi
      ? t(`errores:${error.codigo}`, error.detalles)
      : t('errores:ERR_INTERNO');
}
